import BaseComponent from '../../base/base_component';
import _ from '../../utils/underscore';
import DOM from '../../utils/dom';
import * as OverlayConst from './overlay_const';
import {USER_EVENTS, SYSTEM_EVENTS} from '../../events/events';
import Event from '../../utils/event';
import {TYPES} from '../../services/overlay_manager/overlay_manager_types';
import IOverlayComponentController, { OverlayComponentControllerInitParams } from './overlay_component_controller_interface';
import {PRIORITY_LEVEL_ZINDEX_MAPPER} from "../../services/overlay_manager/overlay_manager_levels";

const overlayContent = require('./view/overlay-content.html');
const closeOverlayStrip = require('./view/overlay-close-strip.html');

export interface IChangePriorityParams {
  className: string;
  newPriority: string;
}

class OverlayComponentController extends BaseComponent implements IOverlayComponentController{
  static CLASS: string = OverlayConst.OVERLAY_COMPONENT_PROPERTIES.CLASS_NAME;
  static DIRECTIVE: string = OverlayConst.OVERLAY_COMPONENT_PROPERTIES.ID;
  protected _selector: string = 'cgp-overlay-component';
  private _initParams: OverlayComponentControllerInitParams = null;

  init(params) {
    super.init(params);
    this._subscribe();
    this._setInitParams(params);
    this.setHTML(overlayContent);
  }

  private _subscribe() {
    this.onSystemEvent(SYSTEM_EVENTS.CHANGE_OVERLAY_PRIORITY, this._onChangePriorityEvent);
  }

  private _setInitParams(params): void{
    this._initParams = {
      priority: params.priority,
      id: params.id,
      type: params.type ? params.type : 0,
      additionalClasses: params.additionalClasses ? params.additionalClasses : [],
      contentAdditionalClasses: params.contentAdditionalClasses ? params.contentAdditionalClasses : [],
      btnCloseCallback: _.isFunction(params.btnCloseCallback) ? params.btnCloseCallback : undefined,
      openCallback: _.isFunction(params.openCallback) ? params.openCallback : undefined,
      isCloseByEscape: !_.isNullOrUndefined(params.isCloseByEscape) ? params.isCloseByEscape : true,
      isCloseBtnAllowed: !_.isNullOrUndefined(params.isCloseBtnAllowed) ? params.isCloseBtnAllowed : true,
      awaitForDisplay: params.awaitForDisplay,
      isHasHeader: !_.isNullOrUndefined(params.isHasHeader) ? params.isHasHeader : true,
    };
  }

  //Create overlay if one does not exist
  public start(): OverlayComponentController {
    const overlayElement = DOM.$$(this._getOverlayClassToPriority());

    // Is the overlay element exist on the DOM?
    if (!overlayElement || overlayElement.length === 0) {
      // Overlay element not exist on dom so create overlay document object and
      // append the id and class attributes.
      this.getEl().id = OverlayConst.OVERLAY_DOM_PROPERTIES.ID_NAME + this._initParams.id;
      this.getEl().className = OverlayConst.OVERLAY_DOM_PROPERTIES.CLASS_NAME;

      DOM
        .addClass(this.getEl(), this._getOverlayClassToPriority())
        // .addClass(this.getEl(), OverlayConst.LIGHTBOX_BACKGROUND_COLOR_CLASS)
        .addClass(this.getEl(), this._getOverlayClassToType())
        .addClass(this.getEl(), this._getAdditionalClasses())
        .addClass(this._getOverlayContetEl(), this._getContentAdditionalClasses())
        .addClass(this.getEl(), this._initParams.awaitForDisplay === true ? 'cgp-overlay-await' : 'cgp-overlay-active')
        // Draw the overlay frame container into the DOM.
        .draw(this.getEl());
    }

    return this;
  }

  private _getCloseEl(): HTMLElement {
    const closeBtnDOMElement = DOM.createElement('div', closeOverlayStrip);
    // Add selector for overlay helpers
    DOM.addClass(closeBtnDOMElement, 'cgp-overlay-injected-helpers-close');
    const closeBtnClass = this._initParams.isHasHeader ? 'cgp-themes-icons-x' : 'cgp-themes-icons-x-no-header';
    DOM.addClass(closeBtnDOMElement.querySelectorAll( '.cgp-strip-close-btn')[0], closeBtnClass);
    // Subscribe overlay btn close event.
    DOM.onDOMElementEvent(closeBtnDOMElement.querySelector('.cgp-strip-close-btn'), USER_EVENTS.ON_CLICK, () => {
      const btnCloseFunction = this._initParams.btnCloseCallback;

      if (_.isFunction(btnCloseFunction)) {
        btnCloseFunction();
      }

      Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, {
        priority: this._initParams.priority
      });
    });

    return closeBtnDOMElement;
  }

  public appendBody(el: HTMLElement) {
    if (!_.isElement(el)) {
      this.logger.error('The element most be dom element type!');
      return;
    }

    const overlayContentEl = this._getOverlayContetEl();
    if (this._initParams.isCloseBtnAllowed) {
      const closeEl = this._getCloseEl();
      DOM.appendFirst(overlayContentEl, closeEl);
      DOM.addClass(this.getEl(), 'cgp-overlay-closable');
    }

    DOM.append(overlayContentEl, el);

    const openFunction = this._initParams.openCallback;

    if (_.isFunction(openFunction)) {
      openFunction();
    }
  }

  private _getOverlayContetEl(): Element {
    return this.getEl().querySelectorAll('.' + OverlayConst.OVERLAY_CONTENT_PROPERTIES.CLASS_NAME)[0];
  }

  private _getOverlayClassToPriority() {
    return 'cgp-overlay-' + this._initParams.priority;
  }

  private _getOverlayClassToType(): string {
    const type = this._initParams.type;
    let typeClass;

    switch (type) {
      case TYPES.FLAT:
        typeClass = OverlayConst.TYPES_CLASS_MAP.FLAT;
        break;
      case TYPES.FULL_SCREEN:
        typeClass = OverlayConst.TYPES_CLASS_MAP.FULL_SCREEN;
        break;
    }

    return typeClass;
  }

  private _getAdditionalClasses(): string[] {
    const additionalClasses = this._initParams.additionalClasses;

    if (!_.isArray(additionalClasses) && 0 === additionalClasses.length) {
      this.logger.debug('There are no additional classes to append to the overlay.');
    }

    return additionalClasses;
  }

  private _getContentAdditionalClasses(): string[] {
    const additionalClasses = this._initParams.contentAdditionalClasses;

    if (!additionalClasses || (!_.isArray(additionalClasses) && 0 === additionalClasses.length)) {
      this.logger.debug('There is not additional classes to append on overlay content.');
    }

    return additionalClasses;
  }

  public changeState(isActive: boolean): void {
    if (isActive === true) {
      this._initParams.awaitForDisplay = false;
      DOM.removeClass(this.getEl(), 'cgp-overlay-await');
      DOM.addClass(this.getEl(), 'cgp-overlay-active');
    } else {
      this._initParams.awaitForDisplay = true;
      DOM.addClass(this.getEl(), 'cgp-overlay-await');
      DOM.removeClass(this.getEl(), 'cgp-overlay-active');
    }
  }

  private _setPriority(priority: string) {
    const classToBeRemoved = this._getOverlayClassToPriority();
    this._initParams.priority = priority;
    const classToBeAdded = this._getOverlayClassToPriority();

    DOM.removeClass(this.getEl(), classToBeRemoved)
        .addClass(this.getEl(), classToBeAdded);

    DOM.addStyle(this.getEl(), 'z-index', PRIORITY_LEVEL_ZINDEX_MAPPER[priority].toString());
  }

  private _onChangePriorityEvent = (changePriorityParams: IChangePriorityParams) => {
    //className = OMG-SPECIFIC-CLASS;
    if (this.getEl().classList.contains(changePriorityParams.className)) {
      this._setPriority(changePriorityParams.newPriority);
    }
  }
}

export default OverlayComponentController;